/**
 * Image utility functions for error handling and optimization
 */

/**
 * Handles image load errors by replacing with a placeholder
 * @param {Event} event - The error event
 * @param {string} placeholderSrc - Optional placeholder image source
 */
export const handleImageError = (event, placeholderSrc = null) => {
  const img = event.target;
  
  if (placeholderSrc) {
    img.src = placeholderSrc;
  } else {
    // Create a simple placeholder
    img.style.display = 'none';
    img.alt = 'Image not available';
    
    // Optionally create a placeholder element
    const placeholder = document.createElement('div');
    placeholder.className = 'image-placeholder';
    placeholder.textContent = 'Image not available';
    placeholder.style.cssText = `
      width: 100%;
      height: 200px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f0f0f0;
      color: #999;
      border-radius: 4px;
    `;
    img.parentNode?.insertBefore(placeholder, img);
  }
  
  console.warn('Image failed to load:', img.src);
};

/**
 * Handles video load errors
 * @param {Event} event - The error event
 */
export const handleVideoError = (event) => {
  const video = event.target;
  console.warn('Video failed to load:', video.src);
  
  // Hide video and show error message
  video.style.display = 'none';
  
  const errorMessage = document.createElement('div');
  errorMessage.className = 'video-error-message';
  errorMessage.textContent = 'Video not available';
  errorMessage.style.cssText = `
    width: 100%;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f0f0f0;
    color: #999;
    border-radius: 4px;
  `;
  video.parentNode?.insertBefore(errorMessage, video);
};

/**
 * Preloads an image
 * @param {string} src - Image source URL
 * @returns {Promise} - Promise that resolves when image is loaded
 */
export const preloadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * Checks if an image URL is valid
 * @param {string} url - Image URL to check
 * @returns {Promise<boolean>} - True if image is valid
 */
export const isValidImageUrl = async (url) => {
  try {
    await preloadImage(url);
    return true;
  } catch {
    return false;
  }
};

